import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Container,
    Tabs,
    Grid,
    Typography,
} from "@mui/material";
import { StyledTab } from "./Style/health-chart.style";
import {
    tabsData,
} from "../../chartsJSON/data";
import useBootstrap from "../../hooks/useBootstrap";
import { districtFilter } from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import DateFilterModal from "../screenings/DateFilterModal";
import { getSessionFilter, setSessionFilter } from "../../utils/sessionStorage";

// Import components
import Header from "./components/Header";
import HealthFacilityChart from "./components/HealthFacilityChart";
import ScreeningCharts from "./components/ScreeningCharts";
import HTNDMAgeGroupChart from "./components/HTNDMAgeGroupChart";
import DistrictWiseHTNDMChart from "./components/DistrictWiseHTNDMChart";
import DiseaseTabCharts from "./components/DiseaseTabCharts";
import CancerTabCharts from "./components/CancerTabCharts";
import MetricCards from "./components/MetricCards";

export default function MainDashboard() {

    const navigate = useNavigate();

    const [tabValue, setTabValue] = React.useState(getSessionFilter('tabValue') || null);
    // Always reset selected district to 'ALL' on page load
    const [selectedDistrict, setSelectedDistrict] = React.useState("ALL");
    // Clear any saved district from session storage
    React.useEffect(() => {
        setSessionFilter('selectedDistrict', 'ALL');
    }, []);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [showDateFilter, setShowDateFilter] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    // Initialize filter data with default values, don't use saved filters to reset dates on page load
    const [filterData, setFilterData] = useState(() => {
        // Always use default filter values from districtFilter() to reset dates
        const defaultFilter = districtFilter();
        // Clear any saved filter from session
        setSessionFilter('dashboardFilter', defaultFilter);
        return { ...defaultFilter };
    });

    // Initialize dashboard filter params with default values to reset dates on page load
    const [dashboardFilterParams, setDashboardFilterParams] = useState(() => {
        const defaultFilter = districtFilter();
        const defaultParams = {
            from_date: defaultFilter.from_date,
            to_date: defaultFilter.to_date,
            search_term: '',
            page_limit: 4000,
            skip_count: 0,
            role_type: '',
            village: '',
            district: selectedDistrict === 'ALL' ? '' : selectedDistrict,
            health_facility: ''
        };
        // Clear any saved params from session
        setSessionFilter('dashboardFilterParams', defaultParams);
        return defaultParams;
    });

    // State to track if we're using filtered data
    const [dateRange] = useState(() => {
        const savedFilter = districtFilter();
        return {
            minDate: savedFilter.from_date,
            maxDate: savedFilter.to_date
        };
    });
    const [districtChartData] = useState(null);
    const [diseaseData] = useState([]);
    const {
        loading: districtsLoading,
        districts,
        summaryAll,
        allDistrictCounts,
        ageWiseScreening,
        fetchWithFilters,
        refetch,
    } = useBootstrap();
    const [summaryData, setSummaryData] = useState({});
    const [summaryAllData, setSummaryAllData] = useState({});
    const [allDistrictsChartData, setAllDistrictsChartData] = useState([]);


    const handleAvatarClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("current_fm_id");
        localStorage.removeItem("first_visit");
        localStorage.removeItem("user_data");
        navigate("/");
        handleMenuClose();
    };

    const handleDateFilterClick = () => {
        setShowDateFilter(true);
        handleMenuClose();
    };

    const handleDateFilterApply = async (filters) => {
        setIsLoading(true);
        try {
            // Update local state
            setFilterData(prev => ({
                ...prev,
                ...filters
            }));

            const updatedParams = {
                ...dashboardFilterParams,
                from_date: filters.from_date || dashboardFilterParams.from_date,
                to_date: filters.to_date || dashboardFilterParams.to_date,
                district: filters.district || dashboardFilterParams.district
            };

            setDashboardFilterParams(updatedParams);

            // Save to session storage
            setSessionFilter('dashboardFilter', {
                ...filterData,
                ...filters
            });

            setSessionFilter('dashboardFilterParams', updatedParams);

            // Fetch data with the new filters
            await fetchWithFilters({
                from_date: updatedParams.from_date,
                to_date: updatedParams.to_date,
                district: updatedParams.district || 'ALL'
            });
        } catch (error) {
            console.error('Error applying filters:', error);
        } finally {
            setIsLoading(false);
            setShowDateFilter(false);
        }
    };

    const handleResetFilters = () => {
        setIsLoading(true);
        try {
            // Reset to default filters
            const defaultFilter = districtFilter();

            setFilterData({ ...defaultFilter });

            const defaultParams = {
                from_date: defaultFilter.from_date,
                to_date: defaultFilter.to_date,
                search_term: '',
                page_limit: 4000,
                skip_count: 0,
                role_type: '',
                village: '',
                district: selectedDistrict === 'ALL' ? '' : selectedDistrict,
                health_facility: ''
            };

            setDashboardFilterParams(defaultParams);

            // Save to session storage
            setSessionFilter('dashboardFilter', { ...defaultFilter });
            setSessionFilter('dashboardFilterParams', defaultParams);

            // Reset the data by calling the bootstrap endpoint
            refetch();
        } catch (error) {
            console.error('Error resetting filters:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Save selected district to sessionStorage when it changes
    useEffect(() => {
        if (selectedDistrict) {
            setSessionFilter('selectedDistrict', selectedDistrict);
        }
    }, [selectedDistrict]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setSessionFilter('tabValue', newValue);
    };

    const updateValue = (key, value) => {
        setFilterData((prevState) => {
            const newState = { ...prevState, [key]: value };
            // Save to sessionStorage whenever filter changes
            setSessionFilter('dashboardFilter', newState);

            // Also update dashboard filter params if date range changes
            if (key === 'from_date' || key === 'to_date') {
                const newParams = { ...dashboardFilterParams, [key]: value };
                setDashboardFilterParams(newParams);
                setSessionFilter('dashboardFilterParams', newParams);
            }

            return newState;
        });
    };

    // Bootstrap handles district list; no separate district fetch here.

    // Seed local mirrors when bootstrap data becomes available
    useEffect(() => {
        setSummaryAllData(summaryAll || {});
        setAllDistrictsChartData(allDistrictCounts || []);
    }, [summaryAll, allDistrictCounts]);

    // Compute summaryData locally from bootstrap store when selection changes
    useEffect(() => {
        if (selectedDistrict === 'ALL') {
            setSummaryData(summaryAll || {});
        } else {
            const row = (allDistrictCounts || []).find(r => r?.DISTRICT === selectedDistrict);
            if (row) {
                // Include all relevant fields including age group data
                const {
                    count_screenings, count_villages, count_hfn,
                    count_htn, count_dm,
                    htn_18_29, dm_18_29,
                    htn_30_39, dm_30_39,
                    htn_40_49, dm_40_49,
                    htn_50_59, dm_50_59,
                    htn_60, dm_60
                } = row;

                setSummaryData({
                    count_screenings,
                    count_villages,
                    count_hfn,
                    count_htn,
                    count_dm,
                    htn_18_29, dm_18_29,
                    htn_30_39, dm_30_39,
                    htn_40_49, dm_40_49,
                    htn_50_59, dm_50_59,
                    htn_60, dm_60
                });
            } else {
                setSummaryData({});
            }
        }
    }, [selectedDistrict, summaryAll, allDistrictCounts]);

    // summaryAllData is already seeded from bootstrap above

    // Age groups for the HTN/DM by Age Group chart
    // Age groups for reference (used in prepareAgeGroupData)

    // Prepare HTN and DM data for the age group chart
    const prepareAgeGroupData = useCallback(() => {
        const htnData = [0, 0, 0, 0, 0]; // 18-29, 30-39, 40-49, 50-59, 60+
        const dmData = [0, 0, 0, 0, 0];   // 18-29, 30-39, 40-49, 50-59, 60+

        if (summaryData) {
            if (summaryData.htn_18_29) htnData[0] = Number(summaryData.htn_18_29) || 0;
            if (summaryData.dm_18_29) dmData[0] = Number(summaryData.dm_18_29) || 0;

            if (summaryData.htn_30_39) htnData[1] = Number(summaryData.htn_30_39) || 0;
            if (summaryData.dm_30_39) dmData[1] = Number(summaryData.dm_30_39) || 0;

            if (summaryData.htn_40_49) htnData[2] = Number(summaryData.htn_40_49) || 0;
            if (summaryData.dm_40_49) dmData[2] = Number(summaryData.dm_40_49) || 0;

            if (summaryData.htn_50_59) htnData[3] = Number(summaryData.htn_50_59) || 0;
            if (summaryData.dm_50_59) dmData[3] = Number(summaryData.dm_50_59) || 0;

            if (summaryData.htn_60) htnData[4] = Number(summaryData.htn_60) || 0;
            if (summaryData.dm_60) dmData[4] = Number(summaryData.dm_60) || 0;
        }

        return { htnData, dmData };
    }, [summaryData]);

    // Distinct color palette (20 colors) for per-district pie segments
    const colors = [
        '#2b94e7', '#ff7f0e', '#2ca02c', '#9467bd', "#1d7461",
        '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
        '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
        '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ab', "#9c27b0"
    ];

    // District labels from API (fallback to empty list to avoid crashes)
    const apiDistrictLabels = (districts || []).map(d => (d.label || '').toUpperCase());
    // Remove 'ALL DISTRICTS' from charts
    const labelsForCharts = apiDistrictLabels.filter((n) => n && n !== 'ALL DISTRICTS');

    // Build donut data
    let donutData = [];

    // Map tab labels to their corresponding data keys and labels
    const tabToConditionMap = {
        'HTN': { key: 'count_htn', femaleKey: 'count_htn_sex_f', label: 'HTN' },
        'DM': { key: 'count_dm', femaleKey: 'count_dm_sex_f', label: 'DM' },
        'DM With HTN': { key: 'dm_with_htn', femaleKey: 'count_dm_with_htn_sex_f', label: 'DM With HTN' },
        'CVD': { key: 'count_cvd', femaleKey: 'count_cvd_sex_f', label: 'CVD' },
        'CKD': { key: 'count_ckd', femaleKey: 'count_ckd_sex_f', label: 'CKD' },
        'COPD': { key: 'count_copd', femaleKey: 'count_copd_sex_f', label: 'COPD' },
        'Mental Health': { key: 'count_mental', femaleKey: 'count_mental_sex_f', label: 'Mental Health' },
        'Post Stroke': { key: 'count_stroke', femaleKey: 'count_stroke_sex_f', label: 'Post Stroke' },
        'BMI>=25': { key: 'count_BMI', femaleKey: 'count_bmi_sex_f', label: 'BMI>=25' },
        'Cancer': { key: 'count_cancer', femaleKey: 'count_cancer_sex_f', label: 'Cancer' },
    };

    // Get current tab info
    const currentTab = tabsData.find(tab => tab.value === tabValue);
    const currentTabLabel = currentTab?.label;

    // If we have a valid tab with a label that exists in our mapping
    if (currentTabLabel && tabToConditionMap[currentTabLabel]) {
        const currentCondition = tabToConditionMap[currentTabLabel];

        // Ensure stable color order by following filtered API district labels order
        donutData = labelsForCharts.map((name, idx) => {
            const row = allDistrictsChartData?.find(r => r.DISTRICT === name);
            const value = Number(row?.[currentCondition.key] || 0);
            return {
                id: idx + 1,
                label: name,
                value,
                color: colors[idx % colors.length],
                district: name,
                district_count: value,
                condition: currentCondition.label
            };
        });
    } else if (diseaseData) {
        // Fallback to existing disease endpoints logic for other tabs if needed
        donutData = diseaseData.map((item, idx) => {
            const value = Number(item?.not_on_treatment || 0) + Number(item?.yes_on_treatment || 0);
            return {
                id: idx + 1,
                label: `${item?.district_name?.toUpperCase()}`,
                value: value,
                color: colors[idx % colors.length],
                district: item?.district_name?.toUpperCase(),
                district_count: value
            };
        });
    }

    // For all conditions that are now in the bootstrap data
    let totalValue = 0;
    if (currentTabLabel && tabToConditionMap[currentTabLabel]) {
        const key = tabToConditionMap[currentTabLabel].key;


        if (selectedDistrict !== 'ALL' && selectedDistrict) {
            // For district view, find the selected district's data
            const districtData = allDistrictsChartData?.find(d => d.DISTRICT === selectedDistrict);
            if (districtData) {
                // For HTN and DM, use the district's specific count
                totalValue = Number(districtData[key] || 0);
            } else {
                // Fallback to sum of donut data if district not found
                totalValue = (donutData || []).reduce((sum, item) => sum + (Number(item.value) || 0), 0);
            }
        } else {
            // For 'ALL DISTRICTS' view, use summaryAllData if available
            const allTotal = Number(summaryAllData?.[key] || 0);
            totalValue = !Number.isNaN(allTotal) && allTotal > 0 ?
                allTotal :
                (donutData || []).reduce((sum, item) => sum + (Number(item.value) || 0), 0);
        }
    } else {
        // Default to sum of donut data if no matching tab is found
        totalValue = (donutData || []).reduce((sum, item) => sum + (Number(item.value) || 0), 0);
    }
    const districtMainData = tabValue !== null ? (donutData || []) : (districtChartData || []);

    const sortedDistChartData = (districtMainData || []).slice().sort((a, b) => (a?.district_count || 0) - (b?.district_count || 0));

    let finalDistChartData = sortedDistChartData;
    let finalDonutData = districtMainData;

    if (selectedDistrict !== 'ALL' && selectedDistrict) {
        const findObj = (sortedDistChartData || []).find((item) => item?.district === selectedDistrict);
        if (findObj) {
            finalDistChartData = [findObj];
        }
        const findObj1 = (districtMainData || []).find((item) => item?.district === selectedDistrict);
        const processedData = (finalDonutData || []).map((item) => {
            const isSelected = findObj1?.id === item?.id;
            return {
                ...item,
                value: item.value,
                color: isSelected ? item.color : "#ccc",
            };
        });
        finalDonutData = processedData
        // For HTN/DM tabs, keep denominator as ALL DISTRICTS total; for others use selected district total
        if (!(tabValue === 0 || tabValue === 1)) {
            if (typeof findObj1?.district_count === 'number') {
                totalValue = findObj1.district_count;
            }
        }
    }

    // Calculate percentage based on each district's total screened population
    const finalDonutDataWithPct = (finalDonutData || []).map((item) => {
        // Find the district data to get total screened population
        const districtData = allDistrictsChartData?.find(d => d.DISTRICT === item.district);
        const districtTotalScreened = Number(districtData?.count_screenings || 0);

        // Calculate percentage based on district's total screened population
        const pct = districtTotalScreened > 0 ?
            ((Number(item.value) || 0) / districtTotalScreened) * 100 :
            0;

        return {
            ...item,
            label: `${item.label} (${pct.toFixed(1)}%)`
        };
    });

    if (isLoading) {
        return (<div style={{ width: '98vw', height: '95vh', position: 'absolute', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
            <img style={{ width: 50, height: 50 }} src='./loading.gif' alt='loading' />
        </div>)
    }

    let districtTitle = 'District Wise Screening';

    const districtTitleOptions = {
        0: `HTN Cases by District`,         // HTN
        1: `DM Cases by District`,          // DM
        2: `DM With HTN Cases by District`,          // DM With HTN
        3: `CVD Cases by District`,              // CVD
        4: `CKD Cases by District`,                   // CKD
        5: `COPD Cases by District`,                  // COPD
        6: `Post Stroke Cases by District`,               // Post Stroke
        7: `Mental Health Cases by District`,               // Mental Health
        8: `BMI>=25 Cases by District`,              // Risk of Having NCD (Assumed BMI)
    };

    if (tabValue || tabValue === 0) {
        districtTitle = districtTitleOptions[tabValue]
    }

    // Prepare age group data for the HTN/DM by Age Group chart
    const { htnData: htnAgeGroup, dmData: dmAgeGroup } = prepareAgeGroupData();

    // Define age groups for the charts
    const ageGroups = ['18-29', '30-39', '40-49', '50-59', '60+'];

    // Loader while bootstrap districts are loading
    if (districtsLoading) {
        return (
            <div style={{ width: '98vw', height: '95vh', position: 'absolute', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
                <img style={{ width: 50, height: 50 }} src='./loading.gif' alt='loading' />
            </div>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Header
                selectedDistrict={selectedDistrict}
                setSelectedDistrict={setSelectedDistrict}
                districts={districts}
                districtsLoading={districtsLoading}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                handleAvatarClick={handleAvatarClick}
                handleMenuClose={handleMenuClose}
                handleLogout={handleLogout}
                handleDateFilterClick={handleDateFilterClick}
                tabValue={tabValue}
                setTabValue={setTabValue}
                navigate={navigate}
            />

            <Container maxWidth="2xl">
                <Grid container justifyContent="center">
                    <Grid item xs={12} sm={10} md={8}>
                        <Box display="flex" justifyContent="center" sx={{ mb: 3, mt: 1 }}>
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                {tabsData.map((item, i) => (
                                    <StyledTab label={item.label} value={item.value} key={i} />
                                ))}
                            </Tabs>
                        </Box>
                    </Grid>
                    {(filterData?.from_date && filterData?.to_date) && (
                        <div style={{
                            textAlign: 'center',
                            alignSelf: 'center',
                            marginLeft: '30px',
                            marginBottom: '12px',
                            fontSize: '14px',
                            fontWeight: 800,
                        }}>
                            {`Filter Range: ${moment(filterData?.from_date, 'YYYY-MM-DD').format('DD MMM YYYY')} - ${moment(filterData?.to_date, 'YYYY-MM-DD').format('DD MMM YYYY')}`}
                        </div>
                    )}
                </Grid>

                {/* Metric Cards - Show on home tab and disease tabs */}
                {(tabValue === null || tabValue === 0 || tabValue) && (
                    <MetricCards
                        summaryData={summaryData}
                        districts={districts}
                    />
                )}

                <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
                    {tabValue === 9 ? (
                        // Cancer tab
                        <CancerTabCharts
                            selectedDistrict={selectedDistrict}
                            allDistrictsChartData={allDistrictsChartData}
                            currentTabLabel={currentTabLabel}
                            ageWiseScreeningData={ageWiseScreening}
                        />
                    ) : (tabValue || tabValue === 0) ? (
                        // Disease tabs (1-8) - Show only the age-wise gender chart
                        (() => {

                            return (
                                <DiseaseTabCharts
                                    districtTitle={districtTitle}
                                    finalDonutDataWithPct={finalDonutDataWithPct}
                                    totalValue={totalValue}
                                    finalDistChartData={finalDistChartData}
                                    allDistrictsChartData={allDistrictsChartData}
                                    tabValue={tabValue}
                                    currentTabLabel={currentTabLabel}
                                    tabToConditionMap={tabToConditionMap}
                                    ageWiseScreeningData={ageWiseScreening}
                                    selectedDistrict={selectedDistrict}
                                />
                            );
                        })()
                    ) : (
                        // Home tab (tabValue is null or undefined) - Show all original charts
                        <>
                            {/* 1. HFN/Villages Chart */}
                            <HealthFacilityChart
                                selectedDistrict={selectedDistrict}
                                allDistrictsChartData={allDistrictsChartData}
                            />

                            {/* 2. Screening Charts */}
                            <ScreeningCharts
                                selectedDistrict={selectedDistrict}
                                allDistrictsChartData={allDistrictsChartData}
                                ageGroups={ageGroups}
                            />

                            {/* 3. HTN/DM by Age Group */}
                            <HTNDMAgeGroupChart
                                ageGroups={ageGroups}
                                htnAgeGroup={htnAgeGroup}
                                dmAgeGroup={dmAgeGroup}
                            />

                            {/* 4. District Wise HTN/DM/Other Disease */}
                            <DistrictWiseHTNDMChart
                                selectedDistrict={selectedDistrict}
                                allDistrictsChartData={allDistrictsChartData}
                            />
                        </>
                    )}
                </Grid>

                <Box
                    sx={{ textAlign: "center", py: 2, borderTop: "1px solid #e0e0e0" }}
                >
                    <Typography variant="caption" color="textSecondary">
                        <b>Copyright@2025 Midorinfratech Pvt.Ltd.</b>
                    </Typography>
                </Box>
            </Container>
            {showDateFilter && (
                <DateFilterModal
                    visible={showDateFilter}
                    onDismiss={() => setShowDateFilter(false)}
                    resetFilters={handleResetFilters}
                    applyFilters={handleDateFilterApply}
                    filterData={filterData}
                    updateValue={updateValue}
                    minDate={dateRange.minDate}
                    maxDate={dateRange.maxDate}
                    selectedDistrict={selectedDistrict}
                />
            )}
        </Box>
    );
}
